import React, { useState, useEffect } from 'react';

const LOADING_STEPS = [
  {
    id: 'website-scan',
    title: 'Scanning Your Website',
    subtitle: 'Analyzing your digital presence',
    icon: '🌐',
    duration: 12000,
    facts: [
      'Did you know? 73% of businesses undervalue themselves by 20-40%',
      'Fun fact: The average business sells for 2.5x annual profit',
      'Insight: Recurring revenue can increase valuation by 35%',
    ],
  },
  {
    id: 'market-analysis',
    title: 'Analyzing Market Position',
    subtitle: 'Comparing with industry standards',
    icon: '📊',
    duration: 12000,
    facts: [
      'Market insight: Tech companies typically sell for 3-5x revenue',
      'Industry trend: Healthcare practices see 15% higher multiples',
      'Buyer preference: 89% prefer businesses with digital presence',
    ],
  },
  {
    id: 'financial-calc',
    title: 'Calculating Valuation',
    subtitle: 'Applying advanced algorithms',
    icon: '💰',
    duration: 10000,
    facts: [
      'Valuation tip: Strong growth adds 20-30% to your price',
      'Buyer fact: Strategic buyers pay 25% more than financial buyers',
      'Exit strategy: Planning 2 years ahead increases value by 40%',
    ],
  },
  {
    id: 'report-gen',
    title: 'Generating Your Report',
    subtitle: 'Preparing personalized insights',
    icon: '📈',
    duration: 8000,
    facts: [
      'Report includes: 3 valuation methods for accuracy',
      'Exclusive: Personalized growth recommendations',
      'Bonus: Buyer readiness assessment included',
    ],
  },
];

export default function UnifiedLoadingScreen({ website, financialData }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [totalProgress, setTotalProgress] = useState(0);

  // Calculate total duration
  const totalDuration = LOADING_STEPS.reduce((sum, step) => sum + step.duration, 0);

  useEffect(() => {
    let stepTimer;
    let progressTimer;
    let currentStepStartTime = Date.now();
    
    const step = LOADING_STEPS[currentStep];
    if (!step) return;

    // Update step progress
    progressTimer = setInterval(() => {
      const elapsed = Date.now() - currentStepStartTime;
      const progress = Math.min((elapsed / step.duration) * 100, 100);
      setStepProgress(progress);
      
      // Update total progress
      const previousStepsProgress = LOADING_STEPS
        .slice(0, currentStep)
        .reduce((sum, s) => sum + s.duration, 0);
      const currentStepContribution = Math.min(elapsed, step.duration);
      const totalElapsed = previousStepsProgress + currentStepContribution;
      setTotalProgress(Math.min((totalElapsed / totalDuration) * 100, 100));
    }, 50);

    // Move to next step
    stepTimer = setTimeout(() => {
      if (currentStep < LOADING_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
        setStepProgress(0);
      }
    }, step.duration);

    return () => {
      clearTimeout(stepTimer);
      clearInterval(progressTimer);
    };
  }, [currentStep, totalDuration]);

  // Rotate facts
  useEffect(() => {
    const factTimer = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % 3);
    }, 4000);

    return () => clearInterval(factTimer);
  }, []);

  const currentStepData = LOADING_STEPS[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Main Loading Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="mb-6">
              <span className="bg-gradient-to-r from-blue-50 to-emerald-50 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                AI-Powered Analysis in Progress
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Valuing Your <span className="text-gradient">Business</span>
            </h1>
            <p className="text-lg text-gray-600">
              We're analyzing <span className="font-semibold text-blue-600">{website}</span>
            </p>
          </div>

          {/* Animated Icon Display */}
          <div className="flex justify-center mb-12">
            <div className="relative">
              {/* Rotating background circle */}
              <div className="absolute inset-0 w-32 h-32 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full opacity-20 animate-pulse"></div>
              
              {/* Main icon */}
              <div className="relative w-32 h-32 bg-white rounded-full shadow-xl flex items-center justify-center">
                <div className="text-6xl animate-bounce" style={{ animationDuration: '2s' }}>
                  {currentStepData.icon}
                </div>
              </div>

              {/* Progress ring */}
              <svg className="absolute inset-0 w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="60"
                  stroke="#E5E7EB"
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="60"
                  stroke="url(#gradient)"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 60}`}
                  strokeDashoffset={`${2 * Math.PI * 60 * (1 - stepProgress / 100)}`}
                  strokeLinecap="round"
                  className="transition-all duration-300"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#10B981" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>

          {/* Current Step Info */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentStepData.title}</h2>
            <p className="text-gray-600">{currentStepData.subtitle}</p>
          </div>

          {/* Steps Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              {LOADING_STEPS.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex flex-col items-center flex-1 ${
                    index !== LOADING_STEPS.length - 1 ? 'relative' : ''
                  }`}
                >
                  {/* Connection line */}
                  {index !== LOADING_STEPS.length - 1 && (
                    <div
                      className={`absolute top-5 left-1/2 w-full h-0.5 ${
                        index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  )}
                  
                  {/* Step circle */}
                  <div
                    className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                      index < currentStep
                        ? 'bg-green-500 scale-100'
                        : index === currentStep
                        ? 'bg-blue-600 scale-110 animate-pulse'
                        : 'bg-gray-200 scale-90'
                    }`}
                  >
                    {index < currentStep ? (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span className="text-xs font-bold text-white">{index + 1}</span>
                    )}
                  </div>
                  
                  {/* Step label */}
                  <span
                    className={`text-xs mt-2 font-medium ${
                      index <= currentStep ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    {step.title.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>

            {/* Overall progress bar */}
            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-full transition-all duration-300"
                style={{ width: `${totalProgress}%` }}
              >
                <div className="absolute inset-0 bg-white opacity-30 animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-xs text-gray-600 mb-1">Annual Profit</div>
              <div className="text-lg font-bold text-gray-900">
                ${Number(financialData.preTaxProfit).toLocaleString()}
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-xs text-gray-600 mb-1">Growth Trend</div>
              <div className="text-lg font-bold text-gray-900 capitalize">
                {financialData.growthTrend === 'increasing' ? '📈 Growing' : 
                 financialData.growthTrend === 'flat' ? '➡️ Stable' : '📉 Declining'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-xs text-gray-600 mb-1">Recurring Revenue</div>
              <div className="text-lg font-bold text-gray-900">{financialData.recurringRevenue}%</div>
            </div>
          </div>

          {/* Fun Facts Carousel */}
          <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-4">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md">
                  <span className="text-lg">💡</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Did You Know?</h3>
                <p className="text-sm text-gray-700 transition-all duration-500">
                  {currentStepData.facts[currentFactIndex]}
                </p>
              </div>
            </div>
          </div>

          {/* Estimated Time */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">
              {totalProgress >= 100 ? (
                <>
                  <span className="font-semibold text-blue-600">Finalizing your report...</span>
                  <br />
                  <span className="text-xs">This is taking a bit longer to ensure accuracy</span>
                </>
              ) : (
                <>
                  Estimated time remaining:{' '}
                  <span className="font-semibold">
                    {Math.max(0, Math.ceil((totalDuration - (Math.min(totalProgress, 100) / 100) * totalDuration) / 1000))} seconds
                  </span>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Additional Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="bg-white rounded-xl shadow-lg p-4 text-center transform hover:scale-105 transition-all">
            <div className="text-2xl mb-2">🔒</div>
            <div className="text-sm font-semibold text-gray-900">Bank-Level Security</div>
            <div className="text-xs text-gray-600">Your data is encrypted and secure</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center transform hover:scale-105 transition-all">
            <div className="text-2xl mb-2">🎯</div>
            <div className="text-sm font-semibold text-gray-900">99% Accuracy</div>
            <div className="text-xs text-gray-600">Based on 10,000+ valuations</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center transform hover:scale-105 transition-all">
            <div className="text-2xl mb-2">⚡</div>
            <div className="text-sm font-semibold text-gray-900">Instant Results</div>
            <div className="text-xs text-gray-600">Get your report in seconds</div>
          </div>
        </div>
      </div>
    </div>
  );
}
